import { Tooltip, Tag } from "antd";
import { PrakurtiCirculerLogo } from "../../components/imagepath";
import dayjs from 'dayjs';
import { calculateBilling, getPaymentProgressColors, getPaymentStateColors } from "../../utils/billingUtils";
import colors from "../../utils/colors";
import { formatTime } from "../../utils/helpers/dateHelpers";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return dayjs(dateString).format('DD-MM-YYYY');
  } catch {
    return dateString;
  }
};

// Helper function to format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return dayjs(dateString).format('DD-MM-YYYY HH:mm');
  } catch {
    return dateString;
  }
};

export const getColumns = (params) => [
  // 1. Serial Number with Simulation Indicator
  {
    id: 'sr',
    name: 'Sr',
    selector: row => row.sr,
    width: '70px',
    cell: (row, index) => {
      const currentPage = parseInt(params?.page) || 1;
      const perPage = parseInt(params?.perPage) || 10;
      const calculatedIndex = (currentPage - 1) * perPage + index + 1;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontWeight: 500 }}>{calculatedIndex}</span>
          {row?.isSimulation && (
            <Tooltip title="Simulation Booking" placement="top">
              <img
                src={PrakurtiCirculerLogo}
                alt="simulation"
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                }}
              />
            </Tooltip>
          )}
        </div>
      );
    },
  },

  // 2. Invoice Number
  {
    id: 'invoiceNumber',
    name: 'Invoice #',
    selector: row => row?.invoice?.invoiceNumber || 'N/A',
    sortable: true,
    minWidth: 140,
    cell: row => (
      <span style={{ fontWeight: 600, color: '#1890ff' }}>
        {row?.invoice?.invoiceNumber || 'N/A'}
      </span>
    ),
  },

  // 3. Customer Name & Contact
  {
    id: 'customerName',
    name: 'Customer Details',
    selector: row => row?.additionalInfo?.name || row?.additionalInfo?.customerName || row?.invoice?.customerName || 'N/A',
    sortable: true,
    minWidth: 200,
    cell: row => {
      const name = row?.additionalInfo?.name || row?.additionalInfo?.customerName || row?.invoice?.customerName || 'N/A';
      const phone = row?.additionalInfo?.phoneNo || row?.invoice?.phoneNo || '';
      const regNo = row?.additionalInfo?.registrationNo || '';

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontWeight: 600, color: '#262626' }}>{name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#666' }}>{phone}</span>
            {regNo && (
              <Tag color="purple" style={{ fontSize: '10px', height: '16px', lineHeight: '14px', margin: 0 }}>
                {regNo}
              </Tag>
            )}
          </div>
        </div>
      );
    },
    format: (value, row) => {
      const name = row?.additionalInfo?.name || row?.additionalInfo?.customerName || row?.invoice?.customerName || 'N/A';
      const phone = row?.additionalInfo?.phoneNo || row?.invoice?.phoneNo || '';
      const regNo = row?.additionalInfo?.registrationNo || '';
      return `${name}${phone ? ` (${phone})` : ''}${regNo ? ` [${regNo}]` : ''}`;
    },
  },

  // 4. Booking Type
  {
    id: 'bookingFor',
    name: 'Booking Type',
    selector: row => row?.bookingFor || 'N/A',
    sortable: true,
    minWidth: 140,
    cell: row => {
      const bookingType = row?.bookingFor || 'N/A';
      const typeColors = {
        'Treatment': '#52c41a',      // Green
        'Solution': '#722ed1',       // Purple
        'Service': '#fa8c16',        // Orange
        'Appointment': '#1890ff',    // Blue
        'Consultation': '#1890ff',   // Blue
      };

      return (
        <Tag color={typeColors[bookingType] || 'default'} style={{ margin: 0, fontWeight: 500 }}>
          {bookingType}
        </Tag>
      );
    },
  },

  // 5. Service / Treatment Info
  {
    id: 'serviceInfo',
    name: 'Service / Treatment Details',
    selector: row => {
      const type = row?.bookingFor;
      if (type === 'Appointment') return row?.additionalInfo?.treatment || 'Consultation';
      if (type === 'Service') return row?.additionalInfo?.serviceData?.[0]?.name || row?.additionalInfo?.serviceName || 'N/A';
      if (type === 'Solution') return row?.additionalInfo?.solutionData?.[0]?.solutionName || row?.additionalInfo?.serviceName || 'N/A';
      if (type === 'Treatment') return row?.additionalInfo?.treatmentData?.[0]?.treatmentName || row?.additionalInfo?.serviceName || 'N/A';
      return 'N/A';
    },
    sortable: false,
    minWidth: 220,
    cell: row => {
      const type = row?.bookingFor;
      let items = [];
      let extra = null;

      if (type === 'Appointment') {
        const treatment = row?.additionalInfo?.treatment;
        if (treatment) items.push({ name: treatment });
        const doctor = row?.additionalInfo?.doctorName;
        if (doctor) extra = <span style={{ fontSize: '10px', color: '#1890ff' }}>Dr. {doctor}</span>;
      } else if (type === 'Service') {
        items = (row?.additionalInfo?.serviceData || []).map(s => ({ name: s.name, qty: s.quantity }));
      } else if (type === 'Solution') {
        items = (row?.additionalInfo?.solutionData || []).map(s => ({ name: s.solutionName }));
      } else if (type === 'Treatment') {
        items = (row?.additionalInfo?.treatmentData || []).map(s => ({ name: s.treatmentName }));
      }

      if (items.length === 0) {
        const fallback = row?.additionalInfo?.serviceName;
        if (fallback) items.push({ name: fallback });
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {items.slice(0, 2).map((item, idx) => (
            <div key={idx} style={{ fontSize: '11px', lineHeight: '1.2' }}>
              <span style={{ fontWeight: 500, color: '#262626' }}>{item.name}</span>
              {item.qty && <span style={{ color: '#8c8c8c', marginLeft: '4px' }}>x{item.qty}</span>}
            </div>
          ))}
          {items.length > 2 && <span style={{ fontSize: '10px', color: '#1890ff' }}>+{items.length - 2} more</span>}
          {extra}
        </div>
      );
    },
  },

  // 6. Booking Period
  {
    id: 'bookingDates',
    name: 'Booking/Schedule',
    selector: row => row?.additionalInfo?.startDate || row?.additionalInfo?.date || row?.additionalInfo?.bookingDate || 'N/A',
    sortable: true,
    minWidth: 160,
    cell: row => {
      const type = row?.bookingFor;
      let primary = 'N/A';
      let secondary = null;

      if (type === 'Appointment' || type === 'Service') {
        primary = row?.additionalInfo?.date || row?.additionalInfo?.bookingDate || row?.invoice?.startDate || 'N/A';
        const startTime = formatTime(row?.additionalInfo?.startTime);
        if (startTime) secondary = `${startTime}${row?.additionalInfo?.endTime ? ` - ${formatTime(row.additionalInfo.endTime)}` : ''}`;
      } else {
        const start = row?.additionalInfo?.startDate || row?.invoice?.startDate;
        const end = row?.additionalInfo?.endDate || row?.invoice?.endDate;
        if (start) {
          primary = `${start}${end ? ` → ${end}` : ''}`;
        }
        const days = row?.additionalInfo?.totalDays || row?.invoice?.totalDays;
        if (days) secondary = `${days} ${parseInt(days) === 1 ? 'Day' : 'Days'}`;
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#262626' }}>{primary}</span>
          {secondary && <span style={{ fontSize: '11px', color: '#8c8c8c' }}>{secondary}</span>}
        </div>
      );
    },
  },

  // 7. Order Status
  {
    id: 'orderStatus',
    name: 'Order Status',
    selector: row => row.orderStatus,
    sortable: true,
    minWidth: 120,
    cell: row => {
      const status = row?.orderStatus || 'N/A';
      const statusConfig = {
        'Initialize': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff' },
        'Confirmed': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591' },
        'Completed': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
        'Cancelled': { color: '#ff4d4f', bg: '#fff1f0', border: '#ffa39e' },
      };
      const config = statusConfig[status] || { color: '#8c8c8c', bg: '#f5f5f5', border: '#d9d9d9' };
      return (
        <span style={{
          color: config.color, backgroundColor: config.bg, border: `1px solid ${config.border}`,
          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500
        }}>
          {status}
        </span>
      );
    },
  },

  // 8. Payment Status
  {
    id: 'paymentStatus',
    name: 'Payment Status',
    selector: row => row.paymentStatus,
    sortable: true,
    minWidth: 130,
    cell: row => {
      const status = row?.paymentStatus || 'N/A';
      const billing = calculateBilling(row);

      let displayStatus = status;
      if (status !== 'Failed' && status !== 'Cancelled') {
        displayStatus = billing.isFullyPaid ? 'Completed' : 'Pending';
      }

      const statusConfig = {
        'Pending': { color: '#fa1616', bg: '#fff1f0', border: '#ffa39e' },
        'Processing': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff' },
        'Completed': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
        'Failed': { color: '#ff4d4f', bg: '#fff1f0', border: '#ffa39e' },
      };
      const config = statusConfig[displayStatus] || { color: '#8c8c8c', bg: '#f5f5f5', border: '#d9d9d9' };
      return (
        <span style={{
          color: config.color, backgroundColor: config.bg, border: `1px solid ${config.border}`,
          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500
        }}>
          {displayStatus}
        </span>
      );
    },
  },

  // 9. Payment Details
  {
    id: 'paymentDetails',
    name: 'Payment Details',
    selector: row => row.amount,
    sortable: true,
    cell: row => {
      const billing = calculateBilling(row);
      const { total, discountValue, paid, pending, isFullyPaid, installment } = billing;
      const percentage = total > 0 ? Math.min(100, Math.round((paid / (total - discountValue)) * 100)) : 0;

      const progressStyle = getPaymentProgressColors(percentage);
      const stateStyle = getPaymentStateColors(row);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '140px' }}>
          {/* Status Badge if special state exists */}
          {stateStyle && (
            <div style={{
              fontSize: '10px', padding: '2px 6px', borderRadius: '10px', width: 'fit-content',
              color: stateStyle.color, backgroundColor: stateStyle.bg, border: `1px solid ${stateStyle.border}`,
              fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.5px'
            }}>
              {stateStyle.label}
            </div>
          )}

          {/* Current Installment Amount */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', fontSize: '12px',
            backgroundColor: '#f6ffed', padding: '4px 6px', borderRadius: '4px', border: '1px solid #b7eb8f', marginBottom: '6px'
          }}>
            <span style={{ fontWeight: 600, color: '#389e0d' }}>Collected:</span>
            <span style={{ fontWeight: 700, color: '#389e0d' }}>₹{installment.toLocaleString('en-IN')}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
            <span style={{ color: '#8c8c8c' }}>Total Bill:</span>
            <span style={{ fontWeight: 600 }}>₹{total.toLocaleString('en-IN')}</span>
          </div>

          {discountValue > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
              <span style={{ color: '#8c8c8c' }}>Discount:</span>
              <span style={{ color: '#ff4d4f' }}>-₹{discountValue.toLocaleString('en-IN')}</span>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
            <span style={{ color: '#8c8c8c' }}>Paid So Far:</span>
            <span style={{ color: progressStyle.color, fontWeight: 600 }}>₹{paid.toLocaleString('en-IN')}</span>
          </div>

          {!isFullyPaid && pending > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
              <span style={{ color: '#8c8c8c' }}>Balance Due:</span>
              <span style={{ color: colors.status.danger, fontWeight: 600 }}>₹{pending.toLocaleString('en-IN')}</span>
            </div>
          )}

          {/* Progress Bar with Semantic Colors */}
          <div style={{
            height: '6px', width: '100%', backgroundColor: '#f0f0f0', borderRadius: '3px', marginTop: '6px', overflow: 'hidden',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              height: '100%',
              width: `${percentage}%`,
              backgroundColor: progressStyle.color,
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ fontSize: '9px', textAlign: 'right', color: '#8c8c8c', fontWeight: 600 }}>{percentage}% Paid</div>
        </div>
      );
    },
  },

  // 10. Payment Mode
  {
    id: 'paymentMode',
    name: 'Mode',
    selector: row => row.paymentMode,
    sortable: true,
    width: '120px',
    cell: row => {
      const mode = (row?.paymentMode || row?.additionalInfo?.bookingPaymentType || 'N/A').toLowerCase();
      const isCash = mode.includes('cash');
      const modeColors = {
        'upi': '#722ed1',
        'card': '#1890ff',
        'netbanking': '#1890ff',
        'cash': '#52c41a',
        'online': '#1890ff',
        'offline': '#fa8c16',
        'whatsapp link': '#25d366',
        'email link': '#fa8c16',
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Tag color={modeColors[mode] || 'default'} style={{ margin: 0, fontSize: '10px', textTransform: 'uppercase', textAlign: 'center' }}>
            {mode}
          </Tag>
          {isCash && <span style={{ fontSize: '9px', color: '#52c41a', fontWeight: 600, textAlign: 'center' }}>PHYSICAL CASH</span>}
        </div>
      );
    },
  },

  // 11. Room Details (for Solution/Treatment)
  {
    id: 'roomInfo',
    name: 'Room Info',
    selector: row => row?.additionalInfo?.roomDetails?.[0]?.roomTypeName || '',
    sortable: false,
    minWidth: 150,
    cell: row => {
      const rooms = row?.additionalInfo?.roomDetails || [];
      if (rooms.length === 0) return <span style={{ color: '#d9d9d9' }}>-</span>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {rooms.slice(0, 1).map((room, idx) => (
            <div key={idx} style={{ fontSize: '11px' }}>
              <span style={{ fontWeight: 500 }}>{room.roomTypeName}</span>
              <div style={{ color: '#8c8c8c', fontSize: '10px' }}>{room.occupancyMode}</div>
            </div>
          ))}
          {rooms.length > 1 && <span style={{ fontSize: '10px', color: '#1890ff' }}>+{rooms.length - 1} more</span>}
        </div>
      );
    },
  },

  // 12. Collected By / Responsible Admin
  {
    id: 'collectedBy',
    name: 'Receiver / Admin',
    selector: row => row.updatedByName || row.createdByName || 'System',
    sortable: true,
    minWidth: 150,
    cell: row => {
      const mode = (row?.bookingPaymentType || row?.paymentMode || row?.additionalInfo?.bookingPaymentType || '').toLowerCase();
      const isCash = mode.includes('cash');
      const latestUpdater = row.updatedByName;
      const originalCreator = row.createdByName || row.additionalInfo?.createdByName;
      const email = row.additionalInfo?.adminEmail;

      return (
        <div style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: isCash ? '#f6ffed' : 'transparent',
          border: isCash ? '1px dashed #b7eb8f' : 'none',
          minWidth: '150px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {latestUpdater && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '10px', color: '#8c8c8c', textTransform: 'uppercase', fontWeight: 600 }}>Cash Collected/Updated By:</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: isCash ? '#389e0d' : '#262626' }}>{latestUpdater}</span>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', marginTop: latestUpdater ? '4px' : '0' }}>
              <span style={{ fontSize: '10px', color: '#8c8c8c', textTransform: 'uppercase' }}>Booked/Cash Collected By:</span>
              <span style={{ fontSize: '11px', fontWeight: 500, color: '#595959' }}>{originalCreator || 'System'}</span>
            </div>

            {email && <span style={{ fontSize: '10px', color: '#bfbfbf', fontStyle: 'italic' }}>{email}</span>}
          </div>
        </div>
      );
    },
  },

  // 13. Created At
  {
    id: 'createdAt',
    name: 'Created At',
    selector: row => row.createdAt,
    sortable: true,
    minWidth: 150,
    cell: row => (
      <span style={{ fontSize: '11px', color: '#595959' }}>
        {formatDateTime(row.createdAt)}
      </span>
    ),
  },

  // 14. Order ID (Reference)
  {
    id: 'orderId',
    name: 'Order Reference',
    selector: row => row.orderId,
    sortable: true,
    minWidth: 150,
    cell: row => (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '10px', color: '#bfbfbf', fontFamily: 'monospace' }}>OID: {row.orderId?.slice(-8)}</span>
        <span style={{ fontSize: '10px', color: '#bfbfbf', fontFamily: 'monospace' }}>TRX: {row.transactionId?.slice(-8) || 'N/A'}</span>
      </div>
    ),
  },
];
